import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import EditIcon from "@mui/icons-material/Edit";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {
  useParams,
  Navigate,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { useSelector, useDispatch } from "react-redux";
import { useUserRole } from "../../hooks/useUserRole";
import { useBriefingMetricsSync } from "../../hooks/useBriefingMetricsSync";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUser } from "../../store/slices/session/sessionSelector";
import {
  exportBriefingReport,
  ReportPeriod,
  BriefingReportDownloadError,
} from "../../utils/briefings/exportBriefingReport";
import {
  briefingApi,
  useGetBriefingByIdQuery,
  useGetAssociatedCampaignsQuery,
  useUpdateBriefingStatusMutation,
  useCreateBriefingVersionMutation,
} from "../../store/apis/briefingApi";
import AddAssociatedCampaignDialog from "../../components/briefings/AddAssociatedCampaignDialog";
import AssociatedCampaignsSection from "../../components/briefings/AssociatedCampaignsSection";
import BriefingMetricsSyncPanel from "../../components/briefings/BriefingMetricsSyncPanel";
import BriefingHistoryTab from "../../components/briefings/BriefingHistoryTab";
import DeleteBriefingDialog from "../../components/briefings/DeleteBriefingDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import DuplicateBriefingDialog from "../../components/briefings/DuplicateBriefingDialog";
import VersionEffectiveDateDialog from "../../components/briefings/VersionEffectiveDateDialog";
import ReportPeriodDialog from "../../components/briefings/ReportPeriodDialog";
import { components } from "../../types/api";
import { AssociatedCampaignWithRelations } from "../../store/apis/briefingApi";
import { getAvailableStatusTransitions } from "../../utils/briefingPermissions";
import BriefingStatusSwitcher from "../../components/briefings/BriefingStatusSwitcher";
import { fmtDateShort } from "../../utils/format";
import { longMonth } from "../../utils/briefings/dateRange";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import OrbLoader from "../../components/common/OrbLoader";
import { createNumberFormat, formatFixed } from "../../utils/numberFormat";
import { useGoBack } from "../../hooks/useGoBack";

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
// Money is always shown with exactly two decimals.
const formatMoney = (value: number) => formatFixed(value, 2);
const allowedViewRoles = ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING"] as const;
// Va entre Detalles (0) e Historial (1), pero con valor propio para no mover el
// de Historial.
const PLATFORM_DATA_TAB = 2;
// Report download follows the WIP dashboard visibility: every role but CLIENT.
const reportDownloadRoles = [
  "ADMIN",
  "ACCOUNT",
  "TECHNICIAN",
  "PRICING",
  "STRATEGY",
] as const;
const ALL_BRIEFING_PLATFORMS: BriefingPlatform[] = [
  "BEAPOLO",
  "BEAPOLO_RADIO",
  "BEAPOLO_PODCAST",
  "NATADS",
  "BEAPOLO_AMAZON_PRIME",
  "BEAPOLO_TWITCH",

  "BEAPOLO_DISNEY_PLUS",
  "BEAPOLO_DOOH",
  "BEAPOLO_OOH",
  "BEAPOLO_PRENSA",
  "BEAPOLO_LG",
  "BEAPOLO_MOVISTAR",
  "BEAPOLO_NETFLIX",
  "BEAPOLO_OTT",
  "BEAPOLO_SAMSUNG_TV",
  "BEAPOLO_SPOTIFY",
  "BEAPOLO_SKIN",
  "BEAPOLO_YOUTUBE",
  "BEAPOLO_YOUTUBE_CTV",
  "BEAPOLO_REDDIT",
  "ATRESMEDIA",
  "TRES_CAT",
  "MEDIASET",
  "ADFORM",
  "ADROLL",
  "UNDETERMINED",
  "GOOGLE_ADS_SEM",
  "GOOGLE_ADS_PMAX",
  "GOOGLE_ADS_DEMANDGEN",
  "FACEBOOK",
  "INSTAGRAM",
  "FACEBOOK_AND_INSTAGRAM",
  "X_TWITTER",
  "LINKEDIN",
  "TIKTOK",
  "PINTEREST",
];

/** Separador de la línea de datos bajo el título. */
const MetaDot: React.FC = () => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 3,
      height: 3,
      borderRadius: "50%",
      bgcolor: "text.disabled",
      flexShrink: 0,
    }}
  />
);

/** Cifra con su etiqueta corta encima y, si hace falta, una nota debajo. */
const KpiFigure: React.FC<{
  label: string;
  value: string;
  hint?: string;
  valueColor?: string;
}> = ({ label, value, hint, valueColor }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: 20, md: 24 },
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: "-0.02em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: valueColor ?? "text.primary",
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/** Dato con icono y su etiqueta encima. */
const DetailItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  muted?: boolean;
  children: React.ReactNode;
}> = ({ icon, label, muted, children }) => (
  <Stack
    direction="row"
    spacing={1.25}
    alignItems="flex-start"
    sx={{ minWidth: 0 }}
  >
    <Box
      aria-hidden
      sx={{
        display: "flex",
        flexShrink: 0,
        pt: "1px",
        color: "text.secondary",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: muted ? 400 : 600,
          color: "text.primary",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Typography>
    </Box>
  </Stack>
);

const ViewBriefing: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId?.replace(/['"/]/g, "");
  const collapsed = useSelector(selectSidebarCollapsed);
  const [activeTab, setActiveTab] = useState(0);

  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { canEditEverything, isAccount, role } = useUserRole();

  const canView = role
    ? allowedViewRoles.includes(role as (typeof allowedViewRoles)[number])
    : false;
  const canDownloadReport = role
    ? reportDownloadRoles.includes(role as (typeof reportDownloadRoles)[number])
    : false;

  const { data, isLoading, error, refetch } = useGetBriefingByIdQuery(
    { id: id ?? "" },
    { skip: !canView || !id }
  );
  const {
    data: associatedCampaignResponse,
    isLoading: isLoadingAssociations,
    error: associatedError,
  } = useGetAssociatedCampaignsQuery(
    { id: id ?? "" },
    { skip: !canView || !id }
  );

  const [updateBriefingStatus, { isLoading: isUpdatingStatus }] =
    useUpdateBriefingStatusMutation();
  const [createBriefingVersion, { isLoading: isCreatingVersion }] =
    useCreateBriefingVersionMutation();

  const briefing = data?.data;
  const isFinalized = briefing?.status === "FINALIZED";
  const isArchived = briefing?.status === "ARCHIVED";
  const canEdit =
    (canEditEverything || isAccount) && !isFinalized && !isArchived;
  const canDuplicate = (role === "ADMIN" || role === "ACCOUNT") && !isArchived;
  const canAmend =
    (role === "ADMIN" || role === "ACCOUNT") &&
    briefing?.status !== "DRAFT" &&
    // La versión en UPDATING ya tiene una nueva en curso: se versiona desde esa.
    briefing?.status !== "UPDATING" &&
    !isArchived;
  const canEditBriefingMetadata =
    (role === "ADMIN" || role === "ACCOUNT") && !isArchived;
  const isTechnician = role === "TECHNICIAN";

  const associatedCampaignsData = associatedCampaignResponse?.data as
    | AssociatedCampaignWithRelations[]
    | undefined;

  const associatedCampaigns: AssociatedCampaignWithRelations[] = useMemo(() => {
    return (
      associatedCampaignsData?.map((campaign) => ({
        ...campaign,
        goal: campaign.goal ?? null,
        target: campaign.target ?? null,
        asset: campaign.asset ?? null,
        sync: campaign.sync ?? null,
      })) ?? []
    );
  }, [associatedCampaignsData]);

  const realTotalBudget = useMemo(() => {
    return associatedCampaigns.reduce((sum, campaign) => {
      if (isTechnician) {
        return (
          sum +
          (campaign.realMetrics?.campaignInvestment ??
            campaign.goal?.totalInversion ??
            0)
        );
      } else {
        return (
          sum +
          (campaign.realMetrics?.totalInvestment ??
            campaign.goal?.totalInversion ??
            0)
        );
      }
    }, 0);
  }, [associatedCampaigns, isTechnician]);

  // Dinero: presupuesto frente a lo real que llevan las líneas.
  const budget = briefing?.totalBudget ?? 0;
  const hasReal = associatedCampaigns.length > 0;
  const realColor =
    Math.abs(realTotalBudget - budget) < 0.01
      ? theme.palette.success.main
      : realTotalBudget > budget
        ? theme.palette.error.main
        : theme.palette.warning.main;

  // Periodo: las dos fechas y el mes del briefing con su nombre ("Octubre 2026").
  const periodStart = fmtDateShort(briefing?.campaignsStartDate);
  const periodEnd = fmtDateShort(briefing?.campaignsEndDate);
  const periodMonth =
    briefing?.month && briefing?.year
      ? `${longMonth(briefing.month, i18n.language)} ${briefing.year}`
      : null;
  const periodDays =
    briefing?.campaignsStartDate && briefing?.campaignsEndDate
      ? dayjs(briefing.campaignsEndDate)
          .startOf("day")
          .diff(dayjs(briefing.campaignsStartDate).startOf("day"), "day") + 1
      : null;

  const statusTransitions =
    role && briefing
      ? getAvailableStatusTransitions(
          role,
          briefing.status,
          briefing.briefingType
        )
      : [];

  const kpiCardSx = {
    borderRadius: "16px",
    border: `1px solid ${theme.palette.divider}`,
    bgcolor: "action.hover",
    px: { xs: 2, md: 2.5 },
    py: 2,
    minWidth: 0,
  } as const;

  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [isEffectiveDateDialogOpen, setEffectiveDateDialogOpen] =
    useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isReportPeriodOpen, setReportPeriodOpen] = useState(false);

  const platformOptions = ALL_BRIEFING_PLATFORMS;

  // Al terminar de actualizarse las líneas, recargar el briefing y sus líneas
  // para que los reales que se ven sean los nuevos.
  const handleMetricsRefreshed = useCallback(() => {
    if (id) {
      dispatch(briefingApi.util.invalidateTags([{ type: "Briefings", id }]));
    }
  }, [dispatch, id]);

  // En la página y no en la pestaña: el sondeo y la recarga tienen que seguir aunque se
  // cambie de pestaña, y el contador de la pestaña se ve desde cualquiera.
  const { problems: metricsProblems, inProgress: metricsInProgress } =
    useBriefingMetricsSync(id, handleMetricsRefreshed, { skip: !canView });

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  const handleBack = () => {
    goBack("/briefings");
  };

  const handleEdit = () => {
    if (id) {
      navigate(`/briefings/${id}/edit`);
    }
  };

  // Descarga el report en Excel del periodo elegido: una hoja por sección
  // (totales, resumen por línea, evolución semanal y diaria y desgloses). Puede
  // tardar unos segundos si el briefing tiene muchas líneas.
  const handleDownloadReport = async (period: ReportPeriod) => {
    if (!id || !user?.token) return;
    setIsDownloadingReport(true);
    try {
      await exportBriefingReport(id, user.token, period);
      setReportPeriodOpen(false);
    } catch (err) {
      const status =
        err instanceof BriefingReportDownloadError ? err.status : undefined;
      const message =
        status === 403
          ? t(
              "briefingsView.reportErrorForbidden",
              "You don't have permission to download this report"
            )
          : status === 404
            ? t("briefingsView.reportErrorNotFound", "Briefing not found")
            : t("briefingsView.reportError", "Failed to download the report");
      dispatch(setToastMessage({ message, severity: "error" }));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleStatusChange = async (
    newStatus: components["schemas"]["BriefingStatus"],
    effectiveDate?: string
  ) => {
    if (!id) return;

    if (
      newStatus === "FINALIZED" &&
      briefing &&
      briefing.baseBriefingId &&
      !effectiveDate
    ) {
      setEffectiveDateDialogOpen(true);
      return;
    }

    try {
      await updateBriefingStatus({
        id,
        status: newStatus,
        effectiveDate,
      }).unwrap();
      // Status will update automatically via cache invalidation
    } catch (err) {
      console.error("Failed to update briefing status:", err);
      // Error handling - could add a toast notification here
    }
  };

  const handleCreateVersion = async () => {
    if (!id) return;
    try {
      const response = await createBriefingVersion({ id }).unwrap();
      if (response.data?.id) {
        navigate(`/briefings/${response.data.id}`);
      }
    } catch (err) {
      console.error("Failed to create new version:", err);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack direction="row" justifyContent="center" py={8}>
          <OrbLoader
            size="large"
            label={t("common.loading", { defaultValue: "Cargando…" })}
          />
        </Stack>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          {t("briefingsView.error", "Unable to load briefing")}
        </Alert>
      );
    }

    if (!briefing) {
      return (
        <Alert severity="warning">
          {t("briefingsView.notFound", "Briefing not found")}
        </Alert>
      );
    }

    return (
      <Stack spacing={3}>
        {activeTab === 0 && (
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 2.5, md: 3.5 },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* HEADER: título, datos clave y estado (se cambia desde ahí) */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: 24, md: 30 },
                      fontWeight: 700,
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                      overflowWrap: "anywhere",
                      minWidth: 0,
                    }}
                  >
                    {briefing.name}
                  </Typography>
                  {canEditBriefingMetadata && (
                    <Tooltip title={t("common.edit", "Edit")}>
                      <IconButton
                        onClick={handleEdit}
                        size="small"
                        sx={{ bgcolor: "action.hover", flexShrink: 0 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    columnGap: 1.25,
                    rowGap: 0.75,
                    color: "text.secondary",
                    fontSize: 14,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {briefing.companyImageBase64 ? (
                      <Box
                        component="img"
                        src={
                          briefing.companyImageBase64.startsWith("data:")
                            ? briefing.companyImageBase64
                            : `data:image/png;base64,${briefing.companyImageBase64}`
                        }
                        alt=""
                        sx={{
                          width: 24,
                          height: 24,
                          flexShrink: 0,
                          objectFit: "cover",
                          borderRadius: 9999,
                        }}
                      />
                    ) : (
                      <BusinessRoundedIcon sx={{ fontSize: 18 }} />
                    )}
                    <Typography
                      component="span"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {briefing.companyName ?? briefing.companyId}
                    </Typography>
                  </Stack>
                  <MetaDot />
                  <span>
                    {t(
                      `briefingsCreate.form.briefingTypeOptions.${briefing.briefingType}`
                    )}
                  </span>
                  {briefing.version !== undefined && briefing.version > 1 && (
                    <>
                      <MetaDot />
                      <Chip
                        label={`v${briefing.version}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </>
                  )}
                  {briefing.baseBriefingId && (
                    <>
                      <MetaDot />
                      <Link
                        component={RouterLink}
                        to={`/briefings/${briefing.baseBriefingId}`}
                        sx={{
                          textUnderlineOffset: "4px",
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        {t("briefingsView.viewOriginal", "View Original")}
                      </Link>
                    </>
                  )}
                  {briefing.updatedAt && (
                    <Box
                      component="span"
                      sx={{ ml: "auto", pl: 2, fontSize: 13 }}
                    >
                      {t("briefingsView.updatedShort", "Updated")}{" "}
                      {fmtDateShort(briefing.updatedAt)}
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                  <BriefingStatusSwitcher
                    status={briefing.status}
                    transitions={statusTransitions}
                    disabled={isCreatingVersion || isArchived}
                    loading={isUpdatingStatus}
                    onChange={handleStatusChange}
                  />
                </Box>
              </Box>

              {/* KPI: dinero (presupuesto, real y fee juntos) y periodo */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 2fr) minmax(0, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Box sx={kpiCardSx}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 2.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <KpiFigure
                        label={t(
                          "briefingsCreate.form.totalBudget",
                          "Total budget"
                        )}
                        value={`${formatMoney(budget)} €`}
                      />
                      <Stack
                        direction="row"
                        alignItems="baseline"
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        <Typography
                          sx={{ fontSize: 13, color: "text.secondary" }}
                        >
                          {t("briefingsView.realTotal", "Real")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: hasReal ? realColor : "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {hasReal ? `${formatMoney(realTotalBudget)} €` : "—"}
                        </Typography>
                      </Stack>
                    </Box>
                    <KpiFigure
                      label={t("briefingsCreate.form.fee", "Fee")}
                      value={createNumberFormat({
                        style: "percent",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }).format((briefing.fee ?? 0) / 100)}
                      hint={`${t("briefingsView.totalWithFee", "Total + fee")}: ${formatMoney(
                        budget * (1 + (briefing.fee ?? 0) / 100)
                      )} €`}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...kpiCardSx,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.75,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonthIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: 20, md: 22 },
                        fontWeight: 700,
                        lineHeight: 1.15,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {periodStart || periodEnd
                        ? `${periodStart ?? "…"} – ${periodEnd ?? "…"}`
                        : (periodMonth ?? "-")}
                    </Typography>
                  </Stack>
                  {(periodStart || periodEnd) && (
                    <Typography
                      sx={{ fontSize: 13, color: "text.secondary", pl: 3.25 }}
                    >
                      {[
                        periodMonth,
                        periodDays !== null &&
                          t("briefingsView.periodDays", {
                            count: periodDays,
                            defaultValue: "{{count}} days",
                          }),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* DETALLES: nombre de campaña, marca y notas */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: briefing.imageUrl
                      ? "minmax(0, 1fr) auto"
                      : "minmax(0, 1fr)",
                  },
                  gap: 3,
                  pt: 3,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 2,
                    }}
                  >
                    <DetailItem
                      icon={<CampaignOutlinedIcon fontSize="small" />}
                      label={t(
                        "briefingsCreate.form.campaignName",
                        "Campaign Name"
                      )}
                    >
                      {briefing.campaignName ?? t("common.notAvailable", "N/A")}
                    </DetailItem>
                    <DetailItem
                      icon={<LocalOfferOutlinedIcon fontSize="small" />}
                      label={t(
                        "briefingsCreate.form.brandOrProduct",
                        "Brand/Product"
                      )}
                    >
                      {briefing.brandOrProduct ??
                        t("common.notAvailable", "N/A")}
                    </DetailItem>
                  </Box>
                  <DetailItem
                    icon={<NotesRoundedIcon fontSize="small" />}
                    label={t("briefingsCreate.form.notes", "Notes")}
                    muted
                  >
                    {briefing.notes || "-"}
                  </DetailItem>
                </Stack>

                {briefing.imageUrl && (
                  <Box
                    component="img"
                    src={briefing.imageUrl}
                    alt={t("briefingsView.image", "Briefing Image")}
                    sx={{
                      height: 160,
                      width: "auto",
                      maxWidth: "100%",
                      borderRadius: 2,
                      objectFit: "cover",
                      justifySelf: { xs: "start", md: "end" },
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Stack>
        )}

        {activeTab === PLATFORM_DATA_TAB && id && (
          <BriefingMetricsSyncPanel briefingId={id} />
        )}

        {activeTab === 1 && id && <BriefingHistoryTab briefingId={id} />}
      </Stack>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} - 4px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
          "@media print": {
            ml: 0,
            px: 0,
            py: 0,
            width: "100%",
            maxWidth: "100%",
          },
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {/* One bar: back arrow + tabs on the left, actions on the right.
              Hidden until the briefing has loaded. */}
          <Box
            sx={{
              display: briefing ? "flex" : "none",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: 1, md: 2 },
              borderBottom: 1,
              borderColor: "divider",
              pb: { xs: 1, md: 0 },
              "@media print": { display: "none" },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={t("briefingsCreate.back", "Back")}>
                <IconButton
                  onClick={handleBack}
                  aria-label={t("briefingsCreate.back", "Back")}
                >
                  <KeyboardBackspaceIcon />
                </IconButton>
              </Tooltip>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                aria-label="briefing tabs"
              >
                <Tab
                  value={0}
                  label={t("briefingsView.tabs.details", "Detalles & Campañas")}
                />
                <Tab
                  value={PLATFORM_DATA_TAB}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>
                        {t("briefingsView.tabs.platformData", "Platform data")}
                      </span>
                      {metricsInProgress > 0 ? (
                        <OrbLoader size="inline" />
                      ) : (
                        metricsProblems > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            label={metricsProblems}
                            sx={{ height: 20 }}
                          />
                        )
                      )}
                    </Stack>
                  }
                />
                <Tab
                  value={1}
                  label={t("briefingsView.tabs.history", "Historial")}
                />
              </Tabs>
            </Stack>
            {briefing && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{
                  alignSelf: { xs: "stretch", md: "center" },
                  "@media print": { display: "none" },
                }}
              >
                {canAmend && (
                  <Button
                    // Gris neutro: el color queda para los estados y tipos.
                    variant="contained"
                    disableElevation
                    disabled={isCreatingVersion}
                    onClick={handleCreateVersion}
                    sx={{
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      color: "text.primary",
                      // Sale del color del texto: gris claro en modo claro y
                      // gris oscuro en modo oscuro (la escala grey del tema no
                      // se invierte).
                      bgcolor: alpha(theme.palette.text.primary, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.text.primary, 0.16),
                      },
                    }}
                  >
                    {isCreatingVersion ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("briefingsView.amend", "Amend (New Version)")
                    )}
                  </Button>
                )}

                {/* Secondary actions live in an overflow menu so the bar
                    stays on one line next to the tabs. */}
                <Tooltip title={t("common.moreActions", "More actions")}>
                  <IconButton
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                    aria-label={t("common.moreActions", "More actions")}
                    aria-haspopup="true"
                    sx={{ bgcolor: "action.hover" }}
                  >
                    {isDownloadingReport ? (
                      <CircularProgress size={20} />
                    ) : (
                      <MoreVertIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={moreMenuAnchor}
                  open={Boolean(moreMenuAnchor)}
                  onClose={() => setMoreMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => {
                      setMoreMenuAnchor(null);
                      window.print();
                    }}
                  >
                    <ListItemIcon>
                      <PictureAsPdfIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {t("briefingsView.downloadPdf", "Download PDF")}
                    </ListItemText>
                  </MenuItem>
                  {canDownloadReport && (
                    <MenuItem
                      disabled={isDownloadingReport}
                      onClick={() => {
                        setMoreMenuAnchor(null);
                        setReportPeriodOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <FileDownloadIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        {t("briefingsView.downloadReport", "Download report")}
                      </ListItemText>
                    </MenuItem>
                  )}
                  {canDuplicate && (
                    <MenuItem
                      onClick={() => {
                        setMoreMenuAnchor(null);
                        setDuplicateDialogOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <LibraryAddIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        {t("common.duplicate", "Duplicate")}
                      </ListItemText>
                    </MenuItem>
                  )}
                  {canEdit && canEditBriefingMetadata && <Divider />}
                  {canEdit && canEditBriefingMetadata && (
                    <MenuItem
                      onClick={() => {
                        setMoreMenuAnchor(null);
                        setDeleteDialogOpen(true);
                      }}
                      sx={{ color: "error.main" }}
                    >
                      <ListItemIcon sx={{ color: "error.main" }}>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        {t("common.delete", "Delete")}
                      </ListItemText>
                    </MenuItem>
                  )}
                </Menu>
                <DeleteBriefingDialog
                  briefingId={briefing.id}
                  open={isDeleteDialogOpen}
                  onClose={() => setDeleteDialogOpen(false)}
                  onDeleted={handleBack}
                />
              </Stack>
            )}
          </Box>

          {renderContent()}
        </PageContainer>

        {/* Outside the PageContainer on purpose: this wide table uses the
            full width available. */}
        {briefing && activeTab === 0 && (
          <AssociatedCampaignsSection
            campaigns={associatedCampaigns}
            isLoading={isLoadingAssociations}
            hasError={Boolean(associatedError)}
            briefingId={id ?? ""}
            onCampaignsChange={() => {
              refetch();
            }}
            onAddCampaign={canEdit ? () => setAddDialogOpen(true) : undefined}
            isEditMode={canEdit}
            briefingFee={briefing.fee ?? 0}
            briefingBudget={briefing.totalBudget ?? 0}
            briefingStatus={briefing.status}
            briefing={briefing}
            platformOptions={platformOptions}
          />
        )}
      </Box>
      {briefing && id && (
        <>
          <AddAssociatedCampaignDialog
            open={isAddDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            briefingId={id}
            briefing={briefing}
            platformOptions={platformOptions}
            onSuccess={() => {
              setAddDialogOpen(false);
              refetch();
            }}
          />
          <DuplicateBriefingDialog
            open={isDuplicateDialogOpen}
            onClose={() => setDuplicateDialogOpen(false)}
            briefingId={id}
          />
          <VersionEffectiveDateDialog
            open={isEffectiveDateDialogOpen}
            onClose={() => setEffectiveDateDialogOpen(false)}
            isLoading={isUpdatingStatus}
            onConfirm={(date) => {
              setEffectiveDateDialogOpen(false);
              handleStatusChange("FINALIZED", date.toISOString());
            }}
          />
          <ReportPeriodDialog
            open={isReportPeriodOpen}
            onClose={() => setReportPeriodOpen(false)}
            onDownload={handleDownloadReport}
            isLoading={isDownloadingReport}
          />
        </>
      )}
    </Box>
  );
};

export default ViewBriefing;
